import {
  APP_NAME,
  LINK_PATH,
  LINK_TOKEN_LENGTH,
  MAX_EVENT_TITLE,
  MIN_EVENT_IN_TEXT,
  STEP_EVENT_RESERVE,
} from './config'
import { checkSegment, SEGMENT_SEPTETS, type SegmentCheck, septetsOf, toGsmFriendly } from './gsm'

/**
 * Every text the app sends, in one place. docs/MESSAGING-COMPLIANCE.md quotes these; keep the
 * two in step. Each one must be a single GSM-7 segment (gsm.ts), link included. Texts that
 * concern one event carry a fresh contact link; the keyword replies (HELP, STOP, START) do not,
 * because they answer anyone, including numbers we do not know.
 *
 * Event names can be longer than a text has room for (MAX_EVENT_TITLE): each template that
 * names the event shortens it to the room left, with "..." (shortenToFit), so the text stays
 * one segment. Pages always show the full name.
 */

/**
 * Which opt-in confirmation wording to send (SMS_CONFIRM_STYLE). 'long' is the wording in the
 * submitted Twilio toll-free verification; 'short' is the one to switch to once that is
 * approved (docs/MESSAGING-COMPLIANCE.md). It leaves more room for the event name.
 */
export type ConfirmStyle = 'long' | 'short'
export const CONFIRM_STYLES: readonly ConfirmStyle[] = ['long', 'short']

export type MessageContext = {
  appName: string
  /** The public origin links are built on, e.g. https://eventping.app. */
  origin: string
  /** Where HELP points people: an email address or a short URL. */
  support: string
  /** The confirmation wording; 'long' when unset. */
  confirmStyle?: ConfirmStyle
}

export function defaultContext(
  origin: string,
  support: string,
  confirmStyle: ConfirmStyle = 'long',
): MessageContext {
  return { appName: APP_NAME, origin, support, confirmStyle }
}

/** Three ASCII dots: GSM-7 has no single ellipsis character. */
export const ELLIPSIS = '...'

function septets(text: string): number {
  let n = 0
  for (const char of text) n += septetsOf(char) ?? 1
  return n
}

/**
 * `text` as is when it costs at most `budget` septets; otherwise cut to fit with "..." on the
 * end, at the last word boundary when that keeps most of what fits (60%), and without
 * trailing spaces or punctuation before the dots.
 */
export function shortenToFit(text: string, budget: number): string {
  if (septets(text) <= budget) return text
  const room = budget - ELLIPSIS.length
  if (room <= 0) return ELLIPSIS.slice(0, Math.max(0, budget))
  const chars = Array.from(text)
  let used = 0
  let n = 0
  while (n < chars.length) {
    const cost = septetsOf(chars[n] as string) ?? 1
    if (used + cost > room) break
    used += cost
    n++
  }
  let cut = chars.slice(0, n).join('')
  if (chars[n] !== ' ') {
    const space = cut.lastIndexOf(' ')
    if (space > 0 && space >= cut.length * 0.6) cut = cut.slice(0, space)
  }
  cut = cut.replace(/[\s.,;:!?'"&(-]+$/, '')
  return `${cut || chars.slice(0, n).join('')}${ELLIPSIS}`
}

/**
 * Renders a text that names the event, shortening the name to the septets the rest leaves.
 * In the step text that is at least STEP_EVENT_RESERVE, because step names and notes are
 * budgeted against it (checkStepTitle, checkStepNote); if the name was renamed since, it just
 * shrinks further rather than spill into a second segment.
 */
function withEvent(event: string, render: (event: string) => string): string {
  return render(shortenToFit(event, SEGMENT_SEPTETS - septets(render(''))))
}

/** "<event>." without a doubled dot when the name ends in one (or was shortened). */
function sentence(event: string): string {
  return event.endsWith('.') ? event : `${event}.`
}

/** "#ABCD ", shown only when the recipient is in more than one active event. */
export function codePrefix(code: string | null): string {
  return code ? `#${code} ` : ''
}

export function linkUrl(ctx: MessageContext, token: string): string {
  return `${ctx.origin}${LINK_PATH}${token}`
}

/** The longest link a message can carry: what budgets are computed with. */
export function worstLink(ctx: MessageContext): string {
  return linkUrl(ctx, 'x'.repeat(LINK_TOKEN_LENGTH))
}

/** Worst-case prefix: a 4-character code. */
const WORST_PREFIX = codePrefix('XXXX')

type StepParts = { prefix: string; event: string; step: string; note?: string | null; link: string }

/** The step text with the event name as given (templates.step shortens it first). */
function stepText(a: StepParts): string {
  return `${a.prefix}${a.event}: ${a.step}${a.note ? `. ${a.note}` : ''}\n${a.link}`
}

export const templates = {
  /**
   * The one text a web-form sign-up gets, right after it: the opt-in confirmation. Nobody is
   * ever texted first; every number EventPing texts signed itself up on a join page.
   */
  confirm: (ctx: MessageContext, a: { event: string; link: string }) =>
    withEvent(a.event, (event) =>
      ctx.confirmStyle === 'short'
        ? `${ctx.appName}: Following ${sentence(event)} Msg freq varies. Reply STOP to end, HELP for help. ${a.link}`
        : `${ctx.appName}: You're signed up for updates on ${sentence(event)} Msg freq varies. Reply STOP to opt out, HELP for help. ${a.link}`,
    ),

  /**
   * The one text someone gets when the event's creator turns their texts off and they have no
   * email left: informational, within the consent they gave, with their link (which keeps
   * working, so the live timeline is still theirs). Nothing is texted after it.
   */
  textsOff: (ctx: MessageContext, a: { event: string; link: string }) =>
    withEvent(
      a.event,
      (event) =>
        `${ctx.appName}: Text updates for ${event} are off for you. Follow along here: ${a.link}`,
    ),

  step: (
    _ctx: MessageContext,
    a: { prefix: string; event: string; step: string; note?: string | null; link: string },
  ) => withEvent(a.event, (event) => stepText({ ...a, event })),

  link: (_ctx: MessageContext, a: { prefix: string; event: string; link: string }) =>
    withEvent(a.event, (event) => `${a.prefix}${event}: your updates page is ${a.link}`),

  help: (ctx: MessageContext) =>
    `${ctx.appName}: Updates for events you signed up for. Msg freq varies. Msg & data rates may apply. Reply STOP to opt out. Help: ${ctx.support}`,

  stop: (ctx: MessageContext) =>
    `${ctx.appName}: You're unsubscribed and will get no more texts. Reply START to resume.`,

  start: (ctx: MessageContext) =>
    `${ctx.appName}: You're resubscribed to updates for events you signed up for. Reply HELP for help, STOP to opt out.`,
} as const

export type FieldProblem = { field: string; message: string }

function describe(check: SegmentCheck, what: string): string | null {
  if (check.invalid.length > 0) {
    return `${what} can't include ${check.invalid.join(' ')} (texts allow plain letters, numbers and basic punctuation only)`
  }
  if (check.remaining < 0) {
    return `${what} is ${-check.remaining} character${check.remaining === -1 ? '' : 's'} too long to fit in one text`
  }
  return null
}

/**
 * Whether an event's title and sender name are usable: both plain GSM-7 text, the title at most
 * MAX_EVENT_TITLE characters. Length no longer has to fit a text: each template shortens the
 * name to the room it has (shortenToFit), and assertTemplatesFit guarantees that room at
 * startup. Returns the problems, empty when both are fine.
 */
export function checkEventFields(
  _ctx: MessageContext,
  fields: { title: string; senderName: string },
): FieldProblem[] {
  const problems: FieldProblem[] = []
  const title = toGsmFriendly(fields.title)
  const sender = toGsmFriendly(fields.senderName)

  const senderOnly = checkSegment(sender)
  const senderProblem = senderOnly.invalid.length ? describe(senderOnly, 'Your name') : null
  if (senderProblem) problems.push({ field: 'senderName', message: senderProblem })

  const titleCheck = checkSegment(title)
  const over = Array.from(title).length - MAX_EVENT_TITLE
  const titleProblem = titleCheck.invalid.length
    ? describe(titleCheck, 'The event name')
    : over > 0
      ? `The event name is ${over} character${over === 1 ? '' : 's'} over the ${MAX_EVENT_TITLE}-character limit`
      : null
  if (titleProblem) problems.push({ field: 'title', message: titleProblem })
  return problems
}

/** The event name as step texts are budgeted: shortened to STEP_EVENT_RESERVE septets. */
function stepEvent(eventTitle: string): string {
  return shortenToFit(toGsmFriendly(eventTitle), STEP_EVENT_RESERVE)
}

/**
 * Whether a step title fits the step text at worst case, with no note: the longest prefix,
 * the longest link, and the event name at up to STEP_EVENT_RESERVE septets (a longer one is
 * shortened to that, or less when the step and note leave more room).
 */
export function checkStepTitle(
  ctx: MessageContext,
  eventTitle: string,
  step: string,
): string | null {
  return describe(
    checkSegment(
      stepText({
        prefix: WORST_PREFIX,
        event: stepEvent(eventTitle),
        step: toGsmFriendly(step),
        link: worstLink(ctx),
      }),
    ),
    'The step name',
  )
}

/**
 * The septets a trigger-time note may use on this step (its ". " separator included), and
 * the check of a particular note. The web app shows `remaining` as a live counter.
 */
export function checkStepNote(
  ctx: MessageContext,
  eventTitle: string,
  step: string,
  note: string,
): { problem: string | null; remaining: number } {
  const check = checkSegment(
    stepText({
      prefix: WORST_PREFIX,
      event: stepEvent(eventTitle),
      step: toGsmFriendly(step),
      note: toGsmFriendly(note) || null,
      link: worstLink(ctx),
    }),
  )
  return { problem: describe(check, 'The note'), remaining: check.remaining }
}

/**
 * The septets each event-naming template leaves for the event name under this configuration
 * (the longest link and code prefix). The step text's room is the reserve, at least.
 */
export function eventRoom(ctx: MessageContext): Record<'confirm' | 'textsOff' | 'link', number> {
  const link = worstLink(ctx)
  const room = (text: string) => SEGMENT_SEPTETS - septets(text)
  return {
    // "" + "." is what an empty name renders as: count the dot as part of the rest.
    confirm: room(templates.confirm(ctx, { event: '', link })),
    textsOff: room(templates.textsOff(ctx, { event: '', link })),
    link: room(templates.link(ctx, { prefix: WORST_PREFIX, event: '', link })),
  }
}

/**
 * Checks the fixed templates against this configuration (app name, origin, support contact,
 * confirmation style): the keyword replies fit, every text that names the event has room for
 * at least MIN_EVENT_IN_TEXT septets of it, and the longest event name still yields one
 * segment everywhere. The API calls it at startup and refuses to boot when a rename or a long
 * origin would push a message past one segment.
 */
export function assertTemplatesFit(ctx: MessageContext): void {
  const link = worstLink(ctx)
  const fail = (name: string, detail: string): never => {
    throw new Error(
      `The ${name} text does not fit one GSM-7 segment (${detail}). Shorten APP_NAME, APP_ORIGIN or SUPPORT_CONTACT.`,
    )
  }
  for (const [name, room] of Object.entries(eventRoom(ctx))) {
    if (room < MIN_EVENT_IN_TEXT) {
      fail(name, `${room} septets left for the event name, ${MIN_EVENT_IN_TEXT} needed`)
    }
  }
  // Two-septet characters, so the worst case of the shortening is covered too.
  const longest = '['.repeat(MAX_EVENT_TITLE)
  const samples: [string, string][] = [
    ['help', templates.help(ctx)],
    ['stop', templates.stop(ctx)],
    ['start', templates.start(ctx)],
    ['confirmation (longest event)', templates.confirm(ctx, { event: longest, link })],
    ['texts off (longest event)', templates.textsOff(ctx, { event: longest, link })],
    ['link (longest event)', templates.link(ctx, { prefix: WORST_PREFIX, event: longest, link })],
    [
      'step (longest event, 40-char step)',
      templates.step(ctx, {
        prefix: WORST_PREFIX,
        event: 'E'.repeat(MAX_EVENT_TITLE),
        step: 'S'.repeat(40),
        link,
      }),
    ],
  ]
  for (const [name, text] of samples) {
    const check = checkSegment(text)
    if (!check.ok) {
      fail(
        name,
        `${check.septets} septets${check.invalid.length ? `, invalid: ${check.invalid.join(' ')}` : ''}`,
      )
    }
  }
}
